<template>
  <div class="telegram-settings space-y-4">
    <div v-if="showTimezone" class="tz-card">
      <span class="telegram-section-label">Display timezone</span>
      <p class="text-xs text-slate-600 dark:text-zinc-400 mb-2 leading-snug">
        Dates and times in the app are stored in UTC and shown in this timezone.
      </p>
      <div class="flex items-center gap-2 flex-1 flex-wrap">
        <Select
          v-model="timezone"
          :options="timezoneOptions"
          option-label="label"
          option-value="value"
          filter
          filter-placeholder="Search timezone…"
          placeholder="Select timezone"
          class="flex-1 min-w-[220px]"
        />
        <Button
          label="Save"
          icon="pi pi-check"
          size="small"
          :loading="tzLoading"
          type="button"
          @click="saveTimezone"
        />
      </div>
    </div>

    <div class="telegram-panel">
      <div class="telegram-panel__head">
        <div class="flex items-center gap-3 min-w-0 flex-wrap">
          <span
            class="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-sky-500/15 text-sky-600 dark:text-sky-400"
            aria-hidden="true"
          >
            <i class="pi pi-send text-lg"></i>
          </span>
          <div class="min-w-0">
            <div class="font-semibold text-slate-900 dark:text-zinc-100 text-sm sm:text-base">
              Telegram
            </div>
            <div class="text-xs text-slate-600 dark:text-zinc-300 mt-0.5 hidden sm:block">
              {{ statusOnly ? 'Workflow alerts via Telegram' : 'Workflow alerts in Telegram after you link this chat' }}
            </div>
          </div>
        </div>
        <div class="flex items-center gap-2 flex-wrap w-full sm:w-auto sm:justify-end">
          <Tag v-if="displayConnected" value="Connected" severity="success" />
          <Tag v-else value="Not connected" severity="secondary" />
          <Button
            v-if="telegramStatus.connected && allowDisconnect"
            label="Disconnect"
            icon="pi pi-times"
            severity="danger"
            text
            size="small"
            type="button"
            :loading="disconnectLoading"
            @click="confirmDisconnect"
          />
        </div>
      </div>

      <template v-if="statusOnly">
        <p class="text-sm text-slate-600 dark:text-zinc-300 leading-snug">
          {{ statusOnlyConnected ? 'This employee receives workflow alerts in Telegram.' : 'Telegram is not linked for this employee.' }}
        </p>
      </template>

      <template v-else-if="!telegramStatus.connected">
        <div class="telegram-panel__grid">
          <div class="space-y-2 min-w-0">
            <span class="text-xs font-semibold uppercase tracking-wide text-slate-600 dark:text-zinc-400">
              Open bot
            </span>
            <a
              :href="botDeepLink"
              target="_blank"
              rel="noopener noreferrer"
              class="inline-flex items-center gap-2 text-sky-600 hover:text-sky-500 dark:text-sky-400 font-medium text-sm transition-colors break-all"
            >
              <i class="pi pi-telegram text-lg shrink-0" aria-hidden="true"></i>
              {{ botDisplayLabel }}
            </a>
            <p class="text-xs text-slate-600 dark:text-zinc-300 leading-snug pt-1">
              Then send your work email and the verification code below.
            </p>
          </div>
          <div class="telegram-panel__qr shrink-0">
            <img
              :src="qrSrc"
              alt="QR code to open Telegram bot"
              class="rounded-xl border border-slate-200 dark:border-zinc-600 bg-white p-1.5 shadow-sm dark:shadow-zinc-950/40 dark:ring-1 dark:ring-zinc-600"
              width="128"
              height="128"
              loading="lazy"
            />
          </div>
        </div>

        <div class="telegram-code-box">
          <div class="min-w-0 flex-1">
            <span class="text-xs font-semibold uppercase tracking-wide text-slate-600 dark:text-zinc-400">
              Verification code
            </span>
            <div
              v-if="verificationCode"
              class="mt-2 font-mono text-2xl sm:text-3xl font-bold text-emerald-600 dark:text-emerald-400 tracking-[0.25em] select-all"
            >
              {{ verificationCode }}
            </div>
            <p
              v-else
              class="mt-2 text-sm text-slate-600 dark:text-zinc-300"
            >
              Generate a code, then enter it in the bot within 10 minutes.
            </p>
          </div>
          <Button
            :label="verificationCode ? 'Refresh code' : 'Generate code'"
            :icon="verificationCode ? 'pi pi-refresh' : 'pi pi-link'"
            severity="primary"
            size="small"
            type="button"
            class="telegram-code-action shrink-0"
            :loading="codeLoading"
            @click="generateCode"
          />
        </div>

        <p
          v-if="verificationCode"
          class="text-xs text-slate-600 dark:text-zinc-300 leading-relaxed"
        >
          <template v-if="botUsername">
            Open <strong>@{{ botUsername }}</strong>, send your <strong>account email</strong>, then paste this
            <strong>code</strong> when the bot asks.
          </template>
          <template v-else>
            Open the bot, send your account email, then paste this code when asked.
          </template>
        </p>
      </template>

      <template v-else>
        <div class="telegram-connected-summary space-y-2 mt-4 pt-4 border-t border-slate-200/90 dark:border-zinc-700/80">
          <span class="text-xs font-semibold uppercase tracking-wide text-slate-600 dark:text-zinc-400">
            Your Telegram profile
          </span>
          <a
            v-if="telegramProfileHref"
            :href="telegramProfileHref"
            target="_blank"
            rel="noopener noreferrer"
            class="inline-flex items-center gap-2 text-sky-600 hover:text-sky-500 dark:text-sky-400 font-medium text-sm transition-colors break-all"
          >
            <i class="pi pi-telegram shrink-0 text-lg" aria-hidden="true"></i>
            {{ telegramProfileLinkLabel }}
          </a>
          <p v-else class="text-xs text-slate-600 dark:text-zinc-400 leading-snug">
            Connected. Use Telegram on your device to open this chat with the bot.
          </p>
          <p
            v-if="!allowDisconnect"
            class="text-xs text-slate-600 dark:text-zinc-400 leading-snug pt-1"
          >
            To disconnect Telegram, open
            <button
              type="button"
              class="text-sky-600 hover:text-sky-500 dark:text-sky-400 font-medium underline-offset-2 hover:underline bg-transparent border-0 p-0 cursor-pointer"
              @click="openAccountSettings"
            >
              Account settings
            </button>.
          </p>
        </div>
      </template>
    </div>
  </div>
</template>

<script setup>
import { computed, ref, onMounted } from 'vue';
import { useStore } from 'vuex';
import Button from 'primevue/button';
import Tag from 'primevue/tag';
import Select from 'primevue/select';
import { useConfirm } from 'primevue/useconfirm';
import { useToast } from 'primevue/usetoast';
import { timezoneOptions } from './timezoneOptions.js';
import { getUserTimezone, persistUserTimezone } from '@/utils/userTimezone';
import { useGlobalSettings } from '@/features/settings/composables/useGlobalSettings';

defineOptions({ name: 'TelegramUserSettings' });

const { open: openSettingsModal } = useGlobalSettings();

function openAccountSettings() {
  openSettingsModal('account');
}

const emit = defineEmits(['status-change']);

const props = defineProps({
  // axios.create() returns a callable function (instance), not a plain object
  httpClient: {
    type: [Object, Function],
    required: true,
  },
  telegramAccountPath: {
    type: String,
    default: 'telegram/account',
  },
  showTimezone: {
    type: Boolean,
    default: true,
  },
  fallbackTelegramUrl: {
    type: String,
    default: '',
  },
  allowDisconnect: {
    type: Boolean,
    default: true,
  },
  statusOnly: {
    type: Boolean,
    default: false,
  },
  externalConnected: {
    type: Boolean,
    default: null,
  },
});

const confirm = useConfirm();
const toast = useToast();
const store = useStore();

/** Axios interceptor rejects with `{ message, response }` — not a raw AxiosError. */
function detailFromErr(err) {
  return (
    err?.response?.data?.message ||
    err?.message ||
    ''
  );
}

const timezone = ref('UTC');
const tzLoading = ref(false);

const telegramStatus = ref({
  connected: false,
  verified_at: null,
  telegram_username: null,
  telegram_profile_url: null,
});
const verificationCode = ref('');
const botUsername = ref('');
const codeLoading = ref(false);
const disconnectLoading = ref(false);

const statusOnlyConnected = computed(() => {
  if (props.externalConnected !== null) {
    return !!props.externalConnected;
  }
  return false;
});

const displayConnected = computed(() => {
  if (props.statusOnly) {
    return statusOnlyConnected.value;
  }
  return !!telegramStatus.value.connected;
});

const botDeepLink = computed(() => {
  if (botUsername.value) {
    return `https://t.me/${botUsername.value}`;
  }
  return props.fallbackTelegramUrl || 'https://t.me/';
});

const botDisplayLabel = computed(() => {
  if (botUsername.value) {
    return `@${botUsername.value}`;
  }
  if (props.fallbackTelegramUrl) {
    return props.fallbackTelegramUrl.replace(/^https:\/\//, '');
  }
  return 'Telegram bot';
});

const qrSrc = computed(() => {
  const u = encodeURIComponent(botDeepLink.value);
  return `https://api.qrserver.com/v1/create-qr-code/?size=128x128&data=${u}&format=png&bgcolor=ffffff&color=000000&margin=4`;
});

const telegramProfileHref = computed(() => telegramStatus.value?.telegram_profile_url || '');

const telegramProfileLinkLabel = computed(() => {
  const u = telegramStatus.value?.telegram_username;
  if (u) {
    return `@${u}`;
  }
  return 'Open your Telegram profile';
});

async function postAccount(body) {
  return props.httpClient.post(props.telegramAccountPath, body);
}

const fetchTimezone = async () => {
  if (!props.showTimezone) {
    return;
  }
  const fromStore = store.state.users?.user?.timezone;
  if (fromStore) {
    timezone.value = fromStore;
    return;
  }
  try {
    const res = await postAccount({ action: 'getTimezone' });
    if (res.data?.success) {
      const tz = res.data.data?.timezone || res.data.timezone || 'UTC';
      timezone.value = tz;
      store.commit('users/setMeData', { timezone: tz });
    }
  } catch {
    timezone.value = getUserTimezone();
  }
};

const saveTimezone = async () => {
  tzLoading.value = true;
  try {
    const res = await postAccount({ action: 'updateTimezone', timezone: timezone.value });
    if (res.data?.success) {
      const tz = res.data.data?.timezone || res.data.timezone || timezone.value;
      timezone.value = tz;
      persistUserTimezone(tz);
      store.commit('users/setMeData', { timezone: tz });
      toast.add({ severity: 'success', summary: 'Saved', detail: 'Timezone updated', life: 3000 });
    } else {
      toast.add({ severity: 'error', summary: 'Error', detail: res.data?.message || '', life: 3000 });
    }
  } catch (err) {
    toast.add({
      severity: 'error',
      summary: 'Error',
      detail: detailFromErr(err) || 'Failed to save timezone',
      life: 3000,
    });
  } finally {
    tzLoading.value = false;
  }
};

function emitStatusChange() {
  emit('status-change', {
    connected: !!telegramStatus.value.connected,
    telegram_username: telegramStatus.value.telegram_username ?? null,
    telegram_profile_url: telegramStatus.value.telegram_profile_url ?? null,
  });
}

const fetchTelegramStatus = async () => {
  try {
    const res = await postAccount({ action: 'getStatus' });
    if (res.data?.success && res.data.data) {
      telegramStatus.value = res.data.data;
      botUsername.value = res.data.data.bot_username || '';
      emitStatusChange();
    }
  } catch {
    /* silent */
  }
};

const generateCode = async () => {
  codeLoading.value = true;
  try {
    const res = await postAccount({ action: 'generateCode' });
    if (res.data?.success && res.data.data) {
      verificationCode.value = res.data.data.code;
      botUsername.value = res.data.data.bot_username || '';
    } else {
      toast.add({
        severity: 'error',
        summary: 'Error',
        detail: res.data?.message || 'Failed to generate code',
        life: 4000,
      });
    }
  } catch (err) {
    toast.add({
      severity: 'error',
      summary: 'Error',
      detail: detailFromErr(err) || 'Failed to generate code',
      life: 6000,
    });
  } finally {
    codeLoading.value = false;
  }
};

const doDisconnect = async () => {
  disconnectLoading.value = true;
  try {
    const res = await postAccount({ action: 'disconnect' });
    if (res.data?.success) {
      telegramStatus.value = {
        connected: false,
        verified_at: null,
        telegram_username: null,
        telegram_profile_url: null,
      };
      verificationCode.value = '';
      emitStatusChange();
      toast.add({ severity: 'success', summary: 'Disconnected', detail: 'Telegram unlinked', life: 3000 });
    } else {
      toast.add({ severity: 'error', summary: 'Error', detail: res.data?.message, life: 4000 });
    }
  } catch (err) {
    toast.add({
      severity: 'error',
      summary: 'Error',
      detail: detailFromErr(err) || 'Failed to disconnect',
      life: 4000,
    });
  } finally {
    disconnectLoading.value = false;
  }
};

const confirmDisconnect = () => {
  confirm.require({
    message: 'Disconnect Telegram? You will stop receiving Telegram notifications.',
    header: 'Disconnect Telegram',
    icon: 'pi pi-exclamation-triangle',
    acceptProps: { severity: 'danger' },
    rejectProps: { severity: 'secondary', variant: 'outlined' },
    rejectLabel: 'Cancel',
    acceptLabel: 'Disconnect',
    accept: doDisconnect,
  });
};

onMounted(() => {
  if (!props.statusOnly) {
    fetchTelegramStatus();
  }
  fetchTimezone();
});
</script>

<style scoped>
.tz-card {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.75rem;
  padding: 1rem 1.25rem;
  background: var(--p-surface-card, var(--surface-card));
  border: 1px solid var(--p-surface-border, var(--surface-border));
  border-radius: 0.75rem;
}

.telegram-section-label {
  font-weight: 600;
  min-width: 100px;
  font-size: 0.875rem;
  color: var(--p-text-muted-color, var(--text-muted-color));
}

.telegram-panel {
  border-radius: 0.75rem;
  border: 1px solid rgb(226 232 240 / 0.95);
  background: rgb(255 255 255 / 0.92);
  padding: 1rem 1.25rem 1.25rem;
}

.dark-mode .telegram-panel {
  border-color: rgb(63 63 70 / 0.95);
  background: rgb(39 39 42 / 0.75);
  box-shadow: inset 0 1px 0 0 rgb(63 63 70 / 0.25);
}

.telegram-panel__head {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  margin-bottom: 1rem;
  padding-bottom: 1rem;
  border-bottom: 1px solid rgb(241 245 249 / 0.95);
}

@media (min-width: 640px) {
  .telegram-panel__head {
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
  }
}

.dark-mode .telegram-panel__head {
  border-bottom-color: rgb(63 63 70 / 0.85);
}

.telegram-panel__grid {
  display: grid;
  grid-template-columns: 1fr;
  gap: 1.25rem;
  align-items: start;
}

@media (min-width: 768px) {
  .telegram-panel__grid {
    grid-template-columns: minmax(0, 1fr) auto;
  }

  .telegram-panel__qr {
    justify-self: end;
  }
}

.telegram-code-box {
  display: flex;
  flex-direction: column;
  gap: 1rem;
  margin-top: 1rem;
  padding: 1rem 1.25rem;
  border-radius: 0.75rem;
  border: 1px dashed rgb(203 213 225 / 0.95);
  background: rgb(248 250 252 / 0.9);
}

@media (min-width: 640px) {
  .telegram-code-box {
    flex-direction: row;
    align-items: flex-end;
    justify-content: space-between;
  }
}

.dark-mode .telegram-code-box {
  border-color: rgb(82 82 91 / 0.9);
  background: rgb(24 24 27 / 0.65);
}

/* Prime primary button contrast on dark dashed block */
.dark-mode .telegram-code-action.p-button-primary {
  box-shadow: 0 1px 2px rgb(0 0 0 / 0.35);
}
</style>
